import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from "@heroicons/react/24/outline";

import StockTable from "../../components/stock/StockTable";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useParts from "../../data/useParts";
import LoadingWheel from "../../components/shared/LoadingWheel";
import CollapsibleSection from "../../components/shared/CollapsibleSection";
import PriceFormat from "../../utils/priceFormat";
import classNames from "../../utils/classNames";

type StatColor = "blue" | "emerald" | "indigo" | "sky" | "amber" | "rose";

type StockStatFilterKey =
  | "partsAddedToday"
  | "partsWithoutImages"
  | "partsWithoutMPN"
  | "qcIssues"
  | "partsWithoutLocations"
  | "missingParts";

const filterLabels: Record<StockStatFilterKey, string> = {
  partsAddedToday: "Parts Added Today",
  partsWithoutImages: "Parts Without Images",
  partsWithoutMPN: "Parts Without MPN",
  qcIssues: "Parts With QC Issues",
  partsWithoutLocations: "Parts Without Locations",
  missingParts: "Missing Parts",
};

const colorClasses: Record<StatColor, string> = {
  blue: "bg-blue-100 text-blue-600",
  emerald: "bg-emerald-100 text-emerald-600",
  indigo: "bg-indigo-100 text-indigo-600",
  sky: "bg-sky-100 text-sky-600",
  amber: "bg-amber-100 text-amber-600",
  rose: "bg-rose-100 text-rose-600",
};

const borderClasses: Record<StatColor, string> = {
  blue: "border border-blue-300",
  emerald: "border border-emerald-300",
  indigo: "border border-indigo-300",
  sky: "border border-sky-300",
  amber: "border border-amber-300",
  rose: "border border-rose-300",
};

const ringClasses: Record<StatColor, string> = {
  blue: "ring-2 ring-blue-500",
  emerald: "ring-2 ring-emerald-500",
  indigo: "ring-2 ring-indigo-500",
  sky: "ring-2 ring-sky-500",
  amber: "ring-2 ring-amber-500",
  rose: "ring-2 ring-rose-500",
};

function StockStat({
  value,
  label,
  color,
  onClick,
  active,
}: {
  value: string;
  label: string;
  color: StatColor;
  onClick?: () => void;
  active?: boolean;
}) {
  const clickable = Boolean(onClick);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={classNames(
        "flex flex-col items-center justify-center rounded-lg px-3 py-3 text-center shadow-sm transition-transform",
        colorClasses[color],
        clickable
          ? `cursor-pointer hover:scale-105 hover:shadow-md ${borderClasses[color]}`
          : "cursor-default",
        active ? ringClasses[color] : ""
      )}
    >
      <p className="text-xl font-semibold leading-none">{value}</p>
      <p className="mt-1 text-xs font-medium leading-tight">{label}</p>
    </button>
  );
}

const numberOrDash = (value: number | null | undefined): string =>
  value == null ? "-" : value.toString();

const daysOrNA = (value: number | null | undefined): string =>
  value == null ? "N/A" : `${value.toFixed(0)}d`;

export default function Stock() {
  const { stockStats } = useParts();
  const { setInfo } = usePageNameContext();
  const [statFilter, setStatFilter] = useState<StockStatFilterKey | null>(null);
  const [statsOpen, setStatsOpen] = useState(true);

  useEffect(() => {
    setInfo({
      name: "Stock",
      desc: "View and manage all stock that hasn't yet been sold",
    });
  }, []);

  const stats = stockStats.data?.data;

  // TODO(stock): remove dummy fallbacks once backend ships these fields.
  // Tracked in docs/pending-endpoints.md.
  const totalStockValue = stats?.totalStockValue ?? 124500;
  const partsAddedToday = stats?.partsAddedToday ?? 37;
  const partsWithoutLocations = stats?.partsWithoutLocations ?? 218;
  const missingParts = stats?.missingParts ?? 12;

  // Translate the active stat filter into a body the /part/stock endpoint can
  // use. "partsAddedToday" uses the existing date-range mechanism; the other
  // keys are boolean flags the backend still needs to honour — see
  // docs/pending-endpoints.md.
  const externalFilters = useMemo(() => {
    if (!statFilter) return undefined;

    if (statFilter === "partsAddedToday") {
      const now = DateTime.now().setZone("Europe/London");
      return {
        dateFieldType: "Created",
        startDate: now.startOf("day").toISO(),
        endDate: now.endOf("day").toISO(),
      };
    }

    return { [statFilter]: true };
  }, [statFilter]);

  const handleToggleFilter = (key: StockStatFilterKey) => {
    setStatFilter((current) => (current === key ? null : key));
  };

  const filterBanner = statFilter ? (
    <div className="my-2 flex items-center justify-between rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-700">
      <span>
        Filtered by <span className="font-semibold">{filterLabels[statFilter]}</span>
      </span>
      <button
        type="button"
        onClick={() => setStatFilter(null)}
        className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100"
      >
        <XMarkIcon className="h-4 w-4" /> Clear filter
      </button>
    </div>
  ) : null;

  return (
    <>
      {stockStats.isLoading ? (
        <LoadingWheel />
      ) : (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setStatsOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50"
            aria-expanded={statsOpen}
          >
            <span>{statsOpen ? "Hide stock stats" : "Show stock stats"}</span>
            {statsOpen ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
          <CollapsibleSection isOpen={statsOpen}>
            <div className="mt-2 grid grid-cols-3 gap-2 px-1 pb-2 sm:grid-cols-5 lg:grid-cols-9">
              <StockStat
                value={PriceFormat(totalStockValue)}
                label="Total Value Stock"
                color="blue"
              />
              <StockStat
                value={numberOrDash(stats?.inStock)}
                label="Parts In Stock"
                color="emerald"
              />
              <StockStat
                value={daysOrNA(stats?.averageStockAge)}
                label="Avg Days In Stock"
                color="indigo"
              />
              <StockStat
                value={numberOrDash(partsAddedToday)}
                label="Parts Added Today"
                color="sky"
                onClick={() => handleToggleFilter("partsAddedToday")}
                active={statFilter === "partsAddedToday"}
              />
              <StockStat
                value={numberOrDash(stats?.partsWithoutImages)}
                label="Parts Without Images"
                color="amber"
                onClick={() => handleToggleFilter("partsWithoutImages")}
                active={statFilter === "partsWithoutImages"}
              />
              <StockStat
                value={numberOrDash(stats?.partsWithoutMPN)}
                label="Parts Without MPN"
                color="amber"
                onClick={() => handleToggleFilter("partsWithoutMPN")}
                active={statFilter === "partsWithoutMPN"}
              />
              <StockStat
                value={numberOrDash(stats?.qcIssues)}
                label="Parts With QC Issues"
                color="rose"
                onClick={() => handleToggleFilter("qcIssues")}
                active={statFilter === "qcIssues"}
              />
              <StockStat
                value={numberOrDash(partsWithoutLocations)}
                label="Parts Without Locations"
                color="amber"
                onClick={() => handleToggleFilter("partsWithoutLocations")}
                active={statFilter === "partsWithoutLocations"}
              />
              <StockStat
                value={numberOrDash(missingParts)}
                label="Missing Parts"
                color="rose"
                onClick={() => handleToggleFilter("missingParts")}
                active={statFilter === "missingParts"}
              />
            </div>
          </CollapsibleSection>
        </div>
      )}

      <StockTable externalFilters={externalFilters} filterBanner={filterBanner} editableMpn />
    </>
  );
}
